// Kid mode's root (docs/FILE_SPLIT_RUN1_SPEC.md, run 1): ChildView itself stays here; every tab,
// card and overlay that lived beside it now lives under ./kid/ and is re-exported unchanged below.

import { AppLegalFooter, Celebration, CoinIcon, CopyrightFooter, Icon, PARTY_PIECE_SETS, uploadResizedImage, CharacterPicker, characterPatch } from "./Common.jsx";
import { ArcadeOverlay } from "./ArcadePicker.jsx";
import { KidRateNote } from "./RateChange.jsx";
import { KidNotificationPrompt, NotificationToggle } from "./Onboarding.jsx";
import { DecisionCard } from "./AskToBuy.jsx";
import { CARD_COLORS, CURRENCY_SYMBOL } from "../lib/constants.js";
import { ageBand, track } from "../lib/metrics.js";
import { equippedCosmetic } from "../lib/oinks.js";
import { t as ct } from "../lib/content.js";
import { useBackLayer } from "../lib/backstack.js";
import { clampApr, interestEarnedPence, nextMondayPayoutPence } from "../lib/vaultInterest.js";
import { effectiveAge, isYoungChild } from "../lib/age.js";
import { capStatusForNewEarning, cardGradientStyle, getKidTheme, todayStr, tokensToPence, weekStartOf, kidPopColor } from "../lib/utils.js";
import { MoneyMondayOverlay, VaultGrowSuggestOverlay } from "./kid/Vault.jsx";
import { OinkGiftReceived, OinkShop } from "./kid/OinkShop.jsx";
import { HomeTab } from "./kid/HomeTab.jsx";
import { EarnTab } from "./kid/EarnTab.jsx";
import { RewardsTab } from "./kid/RewardsTab.jsx";
import { LearnTab } from "./kid/LearnTab.jsx";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

export function ChildView({ code, onOpenHelp, child, adults = [], muted, tab, setTab, avatarOpen, setAvatarOpen, earnFocusTasks, setEarnFocusTasks, earnFocusLearn, setEarnFocusLearn, transactions, requests, activities, experienceRewards, learning, habits, habitLog, onToggleHabit, rewardSettings, onSubmitRequest, onRedeemGoal, onRequestTransfer, onCancelTransfer, onClaimReward, onLearnEarn, onVocabWordResult, onEarnOinks, onBuyCosmetic, onEquipCosmetic, onBuyCharacter, onEquipCharacter, onSessionFinished, onAnswerDailyQuestion, badgeCelebrateKey, perfectWeekCelebrateKey, onChangeAvatar, onUpdateGoals, showToast, siblings = [], onGiftOinks, onClearOinkGift, onClearRateNote, onItemResults, onClearLevelUp, onHomeworkGo, onSubmitHomework = null, onAskToBuy = null }) {
  const [photoError, setPhotoError] = useState("");
  const [approvalCelebrate, setApprovalCelebrate] = useState(false);
  // Money Monday overlay (docs/VAULT_INTEREST_SCOPE.md) — null | { pence, earnedPence }.
  // Fired by the effect below when interest transactions this device hasn't seen exist.
  const [moneyMonday, setMoneyMonday] = useState(null);
  // Bumped to open + scroll the vault transfer card on Rewards — wired from the Money
  // Monday overlay's "Add more" CTA and the grow-vault suggestion below (VaultGrowthCard's
  // own button went with that card, LB-385), so a movement into the vault can be started from every surface the
  // interest feature creates. `amount`, when given, pre-fills the transfer input (the
  // suggestion's whole point is "move THIS many, in one tap") — a plain open (Money
  // Monday) passes none, leaving the field exactly as the child left it.
  const [vaultFocus, setVaultFocus] = useState({ key: 0, amount: null });
  function goGrowVault(amount) {
    setTab("rewards");
    setVaultFocus((f) => ({ key: f.key + 1, amount: amount || null }));
  }

  // Grow-vault suggestion ("move X tokens and make Y interest") — a proactive nudge shown
  // when a child has idle tokens sitting outside the vault while interest is running. null
  // | { moveTokens, movePence, weeklyPence }. Suppressed while Money Monday is showing (one
  // overlay at a time), while a vault transfer is already pending (they're already acting),
  // and gated by a weekly cooldown per child so it's a nudge, not nagging.
  const [growSuggest, setGrowSuggest] = useState(null);
  useEffect(() => {
    // Real-browser testing caught this: this effect had no tab gate at all, so it could
    // fire the instant a child landed on Home (or mid-navigation, anywhere) and pop a
    // full-screen overlay right on top of the bottom nav, blocking taps on other tabs —
    // exactly the kind of unprompted, non-contextual interruption this whole feature set
    // has deliberately avoided everywhere else. This is a Rewards-tab nudge; it should
    // only ever appear once the child is already there to see it in context, the same
    // way the Savings Vault card itself only renders there.
    if (tab !== "rewards") return;
    if (moneyMonday) return; // one celebration/overlay at a time
    // earnsVaultInterest check restored (bug fix, Aug 2026) — see ChildHero's showInterestRow.
    // Without it this nudge tells an opted-out child that moving tokens will earn them
    // interest every Money Monday, which is simply false for them.
    const apr = (rewardSettings?.vaultInterest?.enabled && child?.earnsVaultInterest !== false) ? clampApr(rewardSettings.vaultInterest.apr) : null;
    if (!apr || child.tokenBalance <= 0) return;
    if (requests.some((r) => r.requestType === "vault_transfer" && r.status === "pending")) return;
    const rate = rewardSettings?.exchangeRate > 0 ? rewardSettings.exchangeRate : 10;
    // 20% of the token balance, at least 1 token (a positive balance was already required).
    const moveTokens = Math.max(1, Math.round(child.tokenBalance * 0.2));
    const movePence = tokensToPence(moveTokens, rate);
    // The "is this even worth mentioning" threshold: reuse the SAME ≥£1 floor the real
    // payout engine already applies, rather than inventing a second number — if moving this
    // amount wouldn't yet earn a real (non-zero) Money Monday payment, the suggestion would
    // be selling something that isn't true yet.
    const weeklyPence = nextMondayPayoutPence({ vaultBalancePence: (child.vaultBalancePence || 0) + movePence, vaultInterestCarry: child.vaultInterestCarry }, apr);
    if (weeklyPence <= 0) return;
    const seenKey = `lb_growsuggest_${code}_${child.id}:${weekStartOf(todayStr())}`;
    try { if (localStorage.getItem(seenKey) === "1") return; } catch {}
    setGrowSuggest({ moveTokens, movePence, weeklyPence });
    try { localStorage.setItem(seenKey, "1"); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, moneyMonday, child.tokenBalance, child.vaultBalancePence, child.earnsVaultInterest, rewardSettings?.vaultInterest?.enabled, rewardSettings?.vaultInterest?.apr]);
  // Whether the Oink Shop overlay is open — opened only from the Piggington card on Home.
  const [oinkShopOpen, setOinkShopOpen] = useState(false);
  // Whether Piggington's Arcade (R5) is open — opened only from the Arcade card on Home,
  // rendered below alongside the Oink Shop with the same fixed-overlay pattern. Since LB-256
  // the overlay is ArcadeOverlay (warm-up, then a picker for Snack Attack or Pig Mountain). `scTest` is the preview-mode-ONLY E2E hook (scope decision §0.9): 1 heart,
  // forced first hazard, seeded warm-up questions — never honoured against a real household.
  const [arcadeOpen, setArcadeOpen] = useState(false);
  // LB-315: the overlays this view opens over a kid tab, each one back entry. Money Monday
  // and the transfer animation dismiss by the same path as their Done: the award is already
  // in the balance, so back never skips it.
  useBackLayer(!!moneyMonday, () => setMoneyMonday(null));
  useBackLayer(!moneyMonday && !!growSuggest, () => setGrowSuggest(null));
  useBackLayer(oinkShopOpen, () => setOinkShopOpen(false));
  useBackLayer(arcadeOpen, () => setArcadeOpen(false));
  useBackLayer(!!avatarOpen, () => setAvatarOpen(false));
  const arcadeScTest = code === "PREVIEW" && new URLSearchParams(window.location.search).get("scTest") === "1";
  // `&scBoss=1` (preview-only, GREEDY_CROW_SCOPE.md §6): jumps a run straight into a crow
  // wave with SC_BOSS_TEST's short deterministic parameters. Takes precedence over scTest's
  // spawn sequence inside GameScreen; the two combine in specs (scTest still seeds warm-up).
  // LB-329: the value is a WAVE COUNT (`scBoss=2` runs a second deterministic wave once the
  // first crow has left, and so on), so a spec can watch him wear a fresh hat into each wave.
  const arcadeScBossParam = new URLSearchParams(window.location.search).get("scBoss");
  const arcadeScBoss = code === "PREVIEW" && /^[1-9][0-9]?$/.test(arcadeScBossParam || "") ? parseInt(arcadeScBossParam, 10) : false;
  // `&pmTest=1` (preview-only, LB-256): Pig Mountain's E2E hook — a fixed seed and a short
  // slope straight into a long climb, so an untouched run ends in seconds at a known place.
  const arcadePmTest = code === "PREVIEW" && new URLSearchParams(window.location.search).get("pmTest") === "1";
  // A brief pig-confetti burst whenever App bumps badgeCelebrateKey (a new Trophy Cabinet
  // badge just unlocked). A counter prop rather than a boolean so back-to-back unlocks each
  // celebrate; the initial mount (key 0) is skipped so loading the app never fires one.
  const [badgeCelebrate, setBadgeCelebrate] = useState(false);
  useEffect(() => {
    if (!badgeCelebrateKey) return;
    setBadgeCelebrate(true);
    const t = setTimeout(() => setBadgeCelebrate(false), 3200);
    return () => clearTimeout(t);
  }, [badgeCelebrateKey]);
  // Perfect Week (Aug 2026 characters/economy task) — same counter-prop celebration shape as
  // badgeCelebrate just above, bumped by App.jsx the moment the 7th day's answer lands.
  const [perfectWeekCelebrate, setPerfectWeekCelebrate] = useState(false);
  useEffect(() => {
    if (!perfectWeekCelebrateKey) return;
    setPerfectWeekCelebrate(true);
    const t = setTimeout(() => setPerfectWeekCelebrate(false), 3200);
    return () => clearTimeout(t);
  }, [perfectWeekCelebrateKey]);
  // Per-child parent toggle (Parent Mode → Learn & Earn → Oinks card). Absent === on, same
  // `!== false` pattern as the per-game Enabled flags. Off hides every Oink surface at once:
  // the Piggington card, the shop, and the OP rows/lines inside the four games.
  const oinksOn = (learning?.oinksEnabled !== false);
  // The equipped "Party" cosmetic's confetti set, threaded into the four games' results
  // celebrations and the Trophy Cabinet's badge-unlock celebration (2026-08: extended to
  // badges after staging feedback found a purchased party skin never showed there) — but
  // nowhere else. Money celebrations (approvals, vault moves) always keep their default
  // coin confetti; badges aren't money, so Law 1 doesn't apply to them (see lib/oinks.js).
  const partyEmoji = oinksOn ? (PARTY_PIECE_SETS[equippedCosmetic(child, "celebration")] || null) : null;
  const kidTheme = getKidTheme(effectiveAge(child));
  const globalActivities = (activities || []).filter((a) => !a.childId);
  const childActivities = (activities || []).filter((a) => a.childId === child.id);
  const allEarnable = [...globalActivities, ...childActivities];
  // Mirrors the cap enforced in App's submitRequest — used here just to show the child a
  // heads-up banner before they even try to send another request.
  const PENDING_REQUEST_CAP = 5;
  const pendingCount = requests.filter((r) => r.status === "pending").length;
  // Mirrors the spending-cap block enforced in App's submitRequest/onLearnEarn — shown as a
  // heads-up banner here so a child sees why "Did something else", earning-activity pills,
  // and Learn & Earn games aren't paying out right now, rather than only finding out after
  // tapping in. amount 0 just asks "are they already at/over the cap", not "would this
  // specific amount breach it" — that per-amount check still happens at submit time.
  const spendingCapStatus = capStatusForNewEarning(child, transactions, requests, rewardSettings?.exchangeRate, 0);
  // R item 4 (Alan, Aug 2026): the banner itself is unchanged text-wise — only ADDS a
  // minimise affordance, since it's shown on every tab (not just Home) while blocked and
  // was taking up real space on a screen a kid might otherwise want for the Earn tab
  // itself. "Minimised" is remembered, not just for this render — localStorage, keyed by
  // child + the current week's Monday (same boundary weekStartOf/capStatusForNewEarning
  // already use for the cap itself), so last week's dismissal can never suppress this
  // week's fresh cap warning; a new week is a fresh localStorage key with nothing stored
  // yet, defaulting back to expanded with no extra reset logic required.
  const capBannerStorageKey = `capBannerMin:${child.id}:${weekStartOf(todayStr())}`;
  const [capBannerMinimized, setCapBannerMinimized] = useState(() => {
    try { return localStorage.getItem(capBannerStorageKey) === "1"; } catch { return false; }
  });
  // Re-reads (rather than trusting the lazy initial state forever) whenever the key itself
  // changes — i.e. switching to a different child, or a real-time week rollover while the
  // app stays open — so a stale minimised flag from a different child/week can't leak in.
  useEffect(() => {
    try { setCapBannerMinimized(localStorage.getItem(capBannerStorageKey) === "1"); } catch { setCapBannerMinimized(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capBannerStorageKey]);
  function setCapBannerMinimizedPersisted(v) {
    setCapBannerMinimized(v);
    try { localStorage.setItem(capBannerStorageKey, v ? "1" : "0"); } catch {}
  }
  // "Something that requires it to be displayed again" (Alan's spec) — interpreted as
  // actually heading toward earning: switching to the Earn tab, or tapping either Home
  // shortcut that lands there (onOpenEarn/onOpenLearnEarn below already flip
  // earnFocusTasks/earnFocusLearn true in the same click that changes tab). Forces back to
  // expanded rather than toggling, so it's idempotent — re-arriving on the Earn tab
  // repeatedly while still blocked doesn't fight a kid who re-minimises it there on purpose.
  useEffect(() => {
    if ((tab === "earn" || tab === "learn") && spendingCapStatus.blocked) setCapBannerMinimizedPersisted(false); // Learn is also "heading toward earning" (zip 3)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, earnFocusTasks, earnFocusLearn]);
  // Same "at/over the weekly cap" signal as the banner below, reworded for the moment a
  // kid opens a Learn & Earn game specifically — shown on every game's intro screen (see
  // Tests.jsx's weeklyCapMessage prop), taking priority there over each game's own
  // separate per-game payout-cap notice, since the household cap is the more fundamental
  // reason nothing's being credited right now.
  const weeklyCapMessage = spendingCapStatus.blocked
    ? `Practice only — you've reached your ${CURRENCY_SYMBOL}${(child.spendingCap?.weeklyGBP || 0).toFixed(2)} earning limit for the week — chat to your parent if you'd like to do more.`
    : null;

  // Celebrate with the coin animation whenever this child has a request a parent approved
  // that they haven't already seen — covers both opening the app fresh and a live approval
  // landing while it's already open. "Seen" is tracked per device (localStorage), keyed by
  // request id, so re-opening the app never re-celebrates the same approval twice.
  useEffect(() => {
    const seenKey = `lb_celebrated_${code}_${child.id}`;
    const raw = localStorage.getItem(seenKey);
    // A list another family shared (LB-382) is approved by the parent, not asked for by the
    // child, so it is not "your request was approved".
    const approved = requests.filter((r) => r.status === "approved" && r.requestType !== "homework_shared");
    if (raw === null) {
      // First time this device has tracked this child — seed with everything already approved
      // so far without celebrating, so pre-existing history doesn't trigger a party on upgrade.
      try { localStorage.setItem(seenKey, JSON.stringify(approved.map((r) => r.id))); } catch {}
      return;
    }
    let seen;
    try { seen = new Set(JSON.parse(raw)); } catch { seen = new Set(); }
    const newlyApprovedList = approved.filter((r) => !seen.has(r.id));
    if (newlyApprovedList.length > 0) {
      setApprovalCelebrate(true);
      const t = setTimeout(() => setApprovalCelebrate(false), 4400);
      if (showToast) {
        showToast(
          newlyApprovedList.length === 1
            ? ct("toast.request_approved_single", 'Your parent approved "{title}".', { title: newlyApprovedList[0].title })
            : ct("toast.requests_approved_multi", "Your parent approved {count} of your requests.", { count: newlyApprovedList.length }),
          "good"
        );
      }
      approved.forEach((r) => seen.add(r.id));
      try { localStorage.setItem(seenKey, JSON.stringify([...seen])); } catch {}
      return () => clearTimeout(t);
    }
  }, [requests, code, child.id, showToast]);

  // Money Monday celebration — exactly the approval-celebration pattern above: per-device
  // "seen" list in localStorage keyed on interest transaction ids, first run seeds history
  // silently so an upgrade never throws a party for old payments, and multi-week catch-ups
  // celebrate once with the combined total. `?mm=1` (preview-only, same precedent as
  // scTest/scBoss) skips the silent first-run seeding so the overlay is E2E-testable.
  useEffect(() => {
    const interestTxns = transactions.filter((t) => t.interest && t.type === "credit" && !t.reversalOf);
    if (interestTxns.length === 0) return;
    const seenKey = `lb_mm_${code}_${child.id}`;
    const mmTest = code === "PREVIEW" && new URLSearchParams(window.location.search).get("mm") === "1";
    let raw = null;
    try { raw = localStorage.getItem(seenKey); } catch {}
    if (raw === null && !mmTest) {
      try { localStorage.setItem(seenKey, JSON.stringify(interestTxns.map((t) => t.id))); } catch {}
      return;
    }
    let seen;
    try { seen = new Set(JSON.parse(raw || "[]")); } catch { seen = new Set(); }
    const newly = interestTxns.filter((t) => !seen.has(t.id));
    if (newly.length === 0) return;
    setMoneyMonday({
      pence: newly.reduce((sum, t) => sum + (t.amount || 0), 0),
      earnedPence: interestEarnedPence(transactions, child.id),
    });
    interestTxns.forEach((t) => seen.add(t.id));
    try { localStorage.setItem(seenKey, JSON.stringify([...seen])); } catch {}
  }, [transactions, code, child.id]);

  async function handlePhoto(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setPhotoError("");
    try {
      const url = await uploadResizedImage(code, file);
      onChangeAvatar({ avatarPhoto: url });
      setAvatarOpen(false);
    } catch (err) {
      setPhotoError(err.message || "Couldn't use that photo.");
    }
  }

  return (
    <div className="pop relative" data-mode="kid" data-age={isYoungChild(child) ? "young" : "older"} style={{ "--kid-pop": kidPopColor(child.cardColor, kidTheme) }}>
      {/* fixed: this one is not inside a card, so it needs the viewport as its frame or the
          coins fall from the top of the whole scrollable page, out of sight. */}
      {approvalCelebrate && <Celebration variant="coins" count={22} fixed />}
      {moneyMonday && (
        <MoneyMondayOverlay
          child={child}
          pence={moneyMonday.pence}
          earnedPence={moneyMonday.earnedPence}
          canAddMore={(rewardSettings?.vaultInterest?.enabled && child?.earnsVaultInterest !== false && child.tokenBalance > 0) || false}
          onAddMore={() => { setMoneyMonday(null); goGrowVault(); }}
          onDone={() => setMoneyMonday(null)}
        />
      )}
      {!moneyMonday && growSuggest && (
        <VaultGrowSuggestOverlay
          child={child}
          moveTokens={growSuggest.moveTokens}
          movePence={growSuggest.movePence}
          weeklyPence={growSuggest.weeklyPence}
          onMove={() => { setGrowSuggest(null); goGrowVault(growSuggest.moveTokens); }}
          onDismiss={() => setGrowSuggest(null)}
        />
      )}
      {/* fixed, for the same reason as the approval burst above: this is a direct sibling of
          it inside the view's own wrapper, so without it a badge unlock and a Perfect Week
          both drop their pieces off the top of the scrollable page. */}
      {(badgeCelebrate || perfectWeekCelebrate) && <Celebration variant="pig" count={22} emojiOverride={partyEmoji} fixed />}
      {child?.pendingOinkGift && onClearOinkGift && <OinkGiftReceived gift={child.pendingOinkGift} child={child} onClose={onClearOinkGift} />}
      {/* LB-355 (K1–K3): the one-off note after a rate change, after any gift moment. */}
      {!child?.pendingOinkGift && child?.pendingRateNote && onClearRateNote && <KidRateNote note={child.pendingRateNote} child={child} onClose={onClearRateNote} />}
      {avatarOpen && (
        <div className="flex flex-col gap-2 mb-4 bg-white rounded-2xl p-3 shadow-sm border border-slate-100">
          {/* LB-168: a character is the identity — the emoji grid and photo upload are gone. */}
          <div className="text-xs font-medium text-slate-500 mb-1.5">{ct("character.pick.title", "Pick your character")}</div>
          <CharacterPicker profile={child} onPick={(id) => { onChangeAvatar(characterPatch(id)); track("character_picked", { band: ageBand(child) || undefined, kind: id === "pg-base" ? "pig" : id.replace("ch-", "") }); setAvatarOpen(false); }} />
          <p className="text-xs text-slate-500">{ct("character.pick.body", "You can change it any time, and earn more in the Oink Shop.")}</p>
          <div className="pt-2 border-t border-slate-100">
            <div className="text-xs font-medium text-slate-500 mb-1.5">{ct("kid.card_colour", "Card colour")}</div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => onChangeAvatar({ cardColor: null })} className={`w-8 h-8 rounded-full border-2 ${!child.cardColor ? "border-slate-800" : "border-transparent"}`} style={{ background: cardGradientStyle(null, kidTheme) }} title="Default" />
              {Object.entries(CARD_COLORS).map(([key, c]) => (
                <button key={key} onClick={() => onChangeAvatar({ cardColor: key })} className={`w-8 h-8 rounded-full border-2 ${child.cardColor === key ? "border-slate-800" : "border-transparent"}`} style={{ background: `linear-gradient(135deg, ${c.from}, ${c.to})` }} title={c.label} />
              ))}
            </div>
          </div>
          <button className={`w-full mt-1 ${kidTheme.primaryBtn} text-white font-bold text-sm rounded-xl py-2.5`} onClick={() => setAvatarOpen(false)}>{ct("kid.done_button", "Done")}</button>
        </div>
      )}

      {pendingCount >= PENDING_REQUEST_CAP && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-3 py-2.5 mb-3 text-center">
          <span className="text-amber-700 text-sm font-semibold">You've got {PENDING_REQUEST_CAP} requests waiting already — ask your parent to review some before sending more.</span>
        </div>
      )}

      {spendingCapStatus.blocked && (
        capBannerMinimized ? (
          // Whole pill is the tap target (per Alan, Aug 2026 — was chevron-only at first,
          // deliberately, but he wants tap-anywhere for both states, matching the expanded
          // card below). ▼ here means "tap to expand downward", same direction convention
          // CollapsibleSection uses elsewhere (▼ collapsed / ▲ open) — the previous version
          // had this backwards (⌃ on the collapsed pill, ⌄ on the open card).
          <button
            data-testid="cap-banner-pill"
            onClick={() => setCapBannerMinimizedPersisted(false)}
            aria-label="Show the weekly earning limit message"
            className="w-full flex items-center justify-center gap-1.5 bg-rose-50 border border-rose-200 rounded-lg px-3 py-1.5 mb-3 text-xs font-bold text-rose-700"
          >
            <Icon id="ic-attention" size={16} hue="amber" className="inline-block align-[-3px] mr-1.5" />{ct("kid.weekly_limit_reached", "Weekly limit reached")} <Icon id="ic-chevron-down" size={14} className="inline-block align-[-3px]" style={{ color: "currentColor" }} />
          </button>
        ) : (
          // Whole card is the tap target now, not just a small nested chevron button — a
          // nested <button> inside a clickable container is also invalid HTML, so the
          // outer element itself is the button and the chevron is purely decorative.
          // Rose (not amber) deliberately — amber is also the Rewards/goal-progress tone
          // (see ACTIVITY_KIND_STYLE below), and a child was reading "hit my earning limit"
          // as the same signal as "goal progress" (Alan, Aug 2026, Wave 1 fix).
          <button
            data-testid="cap-banner-minimize"
            onClick={() => setCapBannerMinimizedPersisted(true)}
            aria-label="Minimise the weekly earning limit message"
            className="w-full bg-rose-50 border border-rose-200 rounded-2xl px-3 py-2.5 mb-3 flex items-start gap-2 text-left"
          >
            <span className="flex-1 text-rose-700 text-sm font-semibold">You've reached your {CURRENCY_SYMBOL}{(child.spendingCap?.weeklyGBP || 0).toFixed(2)} earning limit for the week — chat to your parent if you'd like to do more.</span>
            <span aria-hidden="true" className="flex-shrink-0 text-rose-700 leading-none px-1 py-0.5"><Icon id="ic-chevron-up" size={16} style={{ color: "currentColor" }} /></span>
          </button>
        )
      )}

      {tab === "home" && (
        <div className="flex flex-col gap-1.5">
          {/* LB-17 follow-up (Alan, 28 Sep): what a grown-up decided, until the child closes it. */}
          <DecisionCard key={child.id} code={code} child={child} requests={requests} adultName={adults.length === 1 ? (adults[0].name || "") : ""} />
          <KidNotificationPrompt code={code} childId={child.id} forcedOff={muted} />
          <HomeTab code={code} child={child} adults={adults} requests={requests} transactions={transactions} onWithdrawRequest={onCancelTransfer} habits={habits} habitLog={habitLog} onToggleHabit={onToggleHabit} onRedeemGoal={onRedeemGoal} rewardSettings={rewardSettings} kidTheme={kidTheme} learning={learning} onOpenEarn={() => { setEarnFocusTasks(true); setTab("earn"); }} onOpenLearnEarn={() => { setEarnFocusLearn(false); setTab("learn"); }} onOpenRewards={() => setTab("rewards")} oinksOn={oinksOn} onOpenOinkShop={() => setOinkShopOpen(true)} onOpenArcade={() => setArcadeOpen(true)} onAnswerDailyQuestion={onAnswerDailyQuestion} badgeCelebrate={badgeCelebrate} perfectWeekCelebrate={perfectWeekCelebrate} capBlocked={spendingCapStatus.blocked} onChangeAvatar={onChangeAvatar} onUpdateGoals={onUpdateGoals} onAskToBuy={onAskToBuy} />
          <NotificationToggle
            code={code}
            role="child"
            childId={child.id}
            asRow
            label="Notify me when a parent replies"
            forcedOff={muted}
            forcedOffReason="A parent turned notifications off for this on this device — they can turn it back on in Parent Mode → Kids."
          />
        </div>
      )}
      {tab === "learn" && (
        <LearnTab code={code} child={child} requests={requests} learning={learning} rewardSettings={rewardSettings} kidTheme={kidTheme} oinksOn={oinksOn} onEarnOinks={onEarnOinks} partyEmoji={partyEmoji} onLearnEarn={onLearnEarn} onItemResults={onItemResults} onVocabWordResult={onVocabWordResult} onSessionFinished={onSessionFinished} weeklyCapMessage={weeklyCapMessage} badgeCelebrateKey={badgeCelebrateKey} onClearLevelUp={onClearLevelUp} onHomeworkGo={onHomeworkGo} onSubmitHomework={onSubmitHomework} />
      )}
      {tab === "earn" && (
        <EarnTab code={code} child={child} requests={requests} allEarnable={allEarnable} habits={habits} habitLog={habitLog} onToggleHabit={onToggleHabit} onSubmitRequest={onSubmitRequest} learning={learning} onLearnEarn={onLearnEarn} onVocabWordResult={onVocabWordResult} rewardSettings={rewardSettings} kidTheme={kidTheme} focusTasks={earnFocusTasks} focusLearnEarn={earnFocusLearn} weeklyCapMessage={weeklyCapMessage} oinksOn={oinksOn} onEarnOinks={onEarnOinks} partyEmoji={partyEmoji} onSessionFinished={onSessionFinished} onItemResults={onItemResults} onOpenLearn={() => setTab("learn")} />
      )}
      {oinkShopOpen && oinksOn && (
        <OinkShop child={child} siblings={siblings} onGift={onGiftOinks} onBuy={onBuyCosmetic} onEquip={onEquipCosmetic} onBuyCharacter={onBuyCharacter} onEquipCharacter={onEquipCharacter} onClose={() => setOinkShopOpen(false)} />
      )}
      {arcadeOpen && (
        <ArcadeOverlay child={child} learning={learning} siblings={siblings} scTest={arcadeScTest} scBoss={arcadeScBoss} pmTest={arcadePmTest} onClose={() => setArcadeOpen(false)} />
      )}
      {tab === "rewards" && (
        <RewardsTab code={code} child={child} requests={requests} transactions={transactions} experienceRewards={experienceRewards} onClaimReward={onClaimReward} onRedeemGoal={onRedeemGoal} onUpdateGoals={onUpdateGoals} onRequestTransfer={onRequestTransfer} onCancelTransfer={onCancelTransfer} rewardSettings={rewardSettings} kidTheme={kidTheme} vaultFocus={vaultFocus} onFocusVault={(amount) => goGrowVault(amount)} onAskToBuy={onAskToBuy} adults={adults} />
      )}

      {/* Rendered here (inside the scrollable content, above the fixed nav) rather than
          relying on the app shell's copy below — that one sits behind the fixed nav in Kid
          Mode and is never visible.

          Zip 28: the LEGAL links now render here too. The app shell skips its own copy
          whenever a child view is mounted, on the stated grounds that "ChildView renders
          its own copy of these" — but it never did, only the copyright line. So the Terms
          and Privacy links a household agreed to have been invisible on every kid screen
          since that change. Same row now carries Help & support, which is how a child
          reaches the report form from anywhere. */}
      <AppLegalFooter onHelp={onOpenHelp ? () => onOpenHelp() : undefined} className="mt-4" />
      <CopyrightFooter />

      {/* Spacer so the last card in any tab never sits behind the fixed nav below. */}
      <div className="h-24" />

      <div className="fixed bottom-0 left-0 right-0 z-20 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.06)] lb-frame-bar md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-full md:max-w-[calc(var(--shell-max)-32px)] md:bottom-3 md:rounded-lg" style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
        <div className="max-w-lg mx-auto px-3 pt-2 pb-2">
          <div className="flex gap-1 md:justify-center">
            {/* F.1 (Alan, Sep 2026): tab icons carry their family colour — Home navy, Earn the
                gold coin, Rewards pink — so the bar isn't three grey glyphs. The active tab
                is the child's pop colour with a white icon; inactive labels stay muted. */}
            {[["home", "ic-home", "Home", "#1B2A4A"], ["earn", null, "Earn", null], ["learn", "ic-brain", "Learn", "#4F46E5"], ["rewards", "ic-gift", "Rewards", "#E86A92"]].map(([t, icon, label, tone]) => (
              <button key={t} onClick={() => { setEarnFocusTasks(false); setEarnFocusLearn(false); setTab(t); }} className={`flex-1 md:flex-none md:w-32 rounded-xl py-2 flex flex-col items-center gap-0.5 ${tab === t ? `${kidTheme.activeTab}` : "text-slate-400"}`}>
                {icon ? <span className="leading-none" aria-hidden="true"><Icon id={icon} size={20} style={{ color: tab === t ? "currentColor" : tone }} /></span> : <CoinIcon className="w-[18px] h-[18px]" />}
                <span className="text-xs font-semibold">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export { KID_TINT_GRADIENTS, CollapsibleSection, DoubleTokenDayBanner, TrophiesSection, KidSectionHead } from "./kid/kidShared.jsx";
export { VaultTransferCard, SavingsVaultCard, MoneyMondayOverlay, VaultTransferOverlay, VaultGrowSuggestOverlay } from "./kid/Vault.jsx";
export { PigCard, OinkGiftReceived, OinkShop } from "./kid/OinkShop.jsx";
export { DailyQuestionCard } from "./kid/DailyQuestion.jsx";
export { kidWelcomeKey, useKidWelcomeDismissed, HomeTab, RecentActivityCard } from "./kid/HomeTab.jsx";
export { StreakCard, DailyTasksSection, ClaimEarningsSection, EarnTab } from "./kid/EarnTab.jsx";
export { WishListPair, RewardsTab } from "./kid/RewardsTab.jsx";
export { LearnEarnTeaser, ADAPTIVE_TEST_SIZE, kidBandLabel, kidLevelLabel, kidLevelOf, comingUpNext, LearnAdvert, LevelUpMoment, LearnTab, LearnEarnTab } from "./kid/LearnTab.jsx";


// Emoji reflecting how well a test session went, shown next to the score on the results screen.
